/**
 * Form for one category node in the Settings > Categories tab: create a new
 * node under a parent, or edit the selected one. Also holds the reorder, move,
 * and deactivate controls for an existing node.
 */

import { useEffect, useMemo, useState } from 'react';
import type { SdAudience, SdKind, SdPriority } from '@ampersandbrands/ui';
import { FilterLabel } from '@/components/FilterLabel';
import { useCreateCategory, useDeleteCategory, useDeskConfig, useUpdateCategory } from '@/lib/useServiceDesk';
import type { SdCategoryAdminNode, SdCategoryInput } from '@/lib/serviceDeskTypes';
import { SdError } from './SdShell';
import { pathLabel, subtreeIds } from './categoryTree';
import {
  ALL_PRIORITIES,
  AUDIENCE_LABELS,
  EDITABLE_AUDIENCES,
  KIND_LABELS,
  PRIORITY_LABELS,
} from './sdLabels';
import { SD_BTN_DANGER, SD_BTN_GHOST, SD_BTN_PRIMARY, SD_INPUT, SD_MUTED } from './sdStyles';

const LEVEL_NAMES = ['Category', 'Sub-category', 'Item'];

function height(node: SdCategoryAdminNode): number {
  return 1 + Math.max(0, ...node.children.map(height));
}

export type CategoryFormTarget =
  | { mode: 'edit'; node: SdCategoryAdminNode }
  | { mode: 'new'; parent: SdCategoryAdminNode | null };

export function CategoryForm({
  target,
  tree,
  siblings,
  onDone,
}: {
  target: CategoryFormTarget;
  tree: SdCategoryAdminNode[];
  /** The nodes sharing this node's parent, in display order (for reordering). */
  siblings: SdCategoryAdminNode[];
  onDone: (selectId: number | null) => void;
}) {
  const node = target.mode === 'edit' ? target.node : null;
  const create = useCreateCategory();
  const update = useUpdateCategory();
  const remove = useDeleteCategory();
  // The vendors a category can send to. While the api has no list, the field stays free text.
  const vendors = useDeskConfig().data?.vendors;

  const [name, setName] = useState(node?.name ?? '');
  const [description, setDescription] = useState(node?.description ?? '');
  const [audience, setAudience] = useState<SdAudience[]>(node?.audience ?? ['corp', 'stores']);
  const [kind, setKind] = useState<SdKind | ''>(node?.kind ?? '');
  const [priority, setPriority] = useState<SdPriority>(node?.default_priority ?? 'normal');
  const [vendorRoute, setVendorRoute] = useState(node?.vendor_route ?? '');
  const [teamHint, setTeamHint] = useState(node?.team_hint ?? '');
  const [pageKey, setPageKey] = useState(node?.page_key ?? '');
  const [hr, setHr] = useState(node?.hr_restricted ?? false);
  const [active, setActive] = useState(node?.is_active ?? true);
  const [moveTo, setMoveTo] = useState('');

  // Reset when a different node is chosen.
  const key = node ? `edit-${node.id}` : `new-${target.mode === 'new' ? (target.parent?.id ?? 'root') : ''}`;
  useEffect(() => {
    setName(node?.name ?? '');
    setDescription(node?.description ?? '');
    setAudience(node?.audience ?? ['corp', 'stores']);
    setKind(node?.kind ?? '');
    setPriority(node?.default_priority ?? 'normal');
    setVendorRoute(node?.vendor_route ?? '');
    setTeamHint(node?.team_hint ?? '');
    setPageKey(node?.page_key ?? '');
    setHr(node?.hr_restricted ?? false);
    setActive(node?.is_active ?? true);
    setMoveTo('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const level = node ? node.level : target.mode === 'new' ? (target.parent ? target.parent.level + 1 : 1) : 1;

  const input: SdCategoryInput = {
    name: name.trim(),
    description: description.trim() || null,
    audience,
    kind: kind || null,
    default_priority: priority,
    vendor_route: vendorRoute.trim() || null,
    team_hint: teamHint.trim() || null,
    page_key: pageKey.trim() || null,
    hr_restricted: hr,
  };

  // Where this node may move: the top level, or under any node with room for its whole branch.
  const moveOptions = useMemo(() => {
    if (!node) return [];
    const blocked = new Set(subtreeIds(node));
    const h = height(node);
    const out: { id: number | null; label: string }[] = [];
    if (node.parent_id !== null) out.push({ id: null, label: 'Top level' });
    const walk = (list: SdCategoryAdminNode[]) => {
      for (const n of list) {
        if (!blocked.has(n.id) && n.level + h <= 3 && n.id !== node.parent_id) {
          out.push({ id: n.id, label: pathLabel(tree, n.id) });
        }
        walk(n.children);
      }
    };
    walk(tree);
    return out;
  }, [node, tree]);

  const idx = node ? siblings.findIndex((s) => s.id === node.id) : -1;

  async function reorder(delta: -1 | 1) {
    if (!node || idx < 0) return;
    const next = [...siblings];
    const swap = idx + delta;
    if (swap < 0 || swap >= next.length) return;
    const a = next[idx];
    const b = next[swap];
    if (!a || !b) return;
    next[idx] = b;
    next[swap] = a;
    // Renumber the whole group so equal or missing sort values cannot trap a node.
    for (let i = 0; i < next.length; i += 1) {
      const n = next[i];
      if (n && n.sort !== (i + 1) * 10) await update.mutateAsync({ id: n.id, patch: { sort: (i + 1) * 10 } });
    }
  }

  const toggle = (a: SdAudience) =>
    setAudience((cur) => (cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a]));

  const canSave = name.trim().length > 0 && audience.length > 0;
  const busy = create.isPending || update.isPending || remove.isPending;

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-base font-semibold text-amp-ink dark:text-amp-dark-ink">
        {node ? `Edit ${(LEVEL_NAMES[level - 1] ?? 'Item').toLowerCase()}` : `New ${(LEVEL_NAMES[level - 1] ?? 'Item').toLowerCase()}`}
        {!node && target.mode === 'new' && target.parent ? ` under ${pathLabel(tree, target.parent.id)}` : ''}
      </h3>

      <div>
        <FilterLabel htmlFor="sd-cat-name">Name</FilterLabel>
        <input id="sd-cat-name" value={name} onChange={(e) => setName(e.target.value)} className={SD_INPUT} />
      </div>
      <div>
        <FilterLabel htmlFor="sd-cat-desc">Description (shown to the person filing)</FilterLabel>
        <textarea
          id="sd-cat-desc"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={SD_INPUT}
        />
      </div>
      <div>
        <FilterLabel as="div">Who can file here</FilterLabel>
        <div className="flex flex-wrap gap-4 text-[13px] text-amp-ink dark:text-amp-dark-ink">
          {EDITABLE_AUDIENCES.map((a) => (
            <label key={a} className="flex items-center gap-1.5">
              <input type="checkbox" checked={audience.includes(a)} onChange={() => toggle(a)} />
              {AUDIENCE_LABELS[a]}
            </label>
          ))}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <FilterLabel htmlFor="sd-cat-kind">Kind of ticket</FilterLabel>
          <select id="sd-cat-kind" value={kind} onChange={(e) => setKind(e.target.value as SdKind | '')} className={SD_INPUT}>
            <option value="">Not set</option>
            {Object.entries(KIND_LABELS).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div>
          <FilterLabel htmlFor="sd-cat-prio">Starting priority</FilterLabel>
          <select
            id="sd-cat-prio"
            value={priority}
            onChange={(e) => setPriority(e.target.value as SdPriority)}
            className={SD_INPUT}
          >
            {ALL_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <FilterLabel htmlFor="sd-cat-vendor">Send to vendor</FilterLabel>
          {vendors && vendors.length > 0 ? (
            <select
              id="sd-cat-vendor"
              value={vendorRoute}
              onChange={(e) => setVendorRoute(e.target.value)}
              className={SD_INPUT}
            >
              <option value="">None</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
              {vendorRoute && !vendors.some((v) => v.id === vendorRoute) && (
                <option value={vendorRoute}>{vendorRoute} (not on the list)</option>
              )}
            </select>
          ) : (
            <input
              id="sd-cat-vendor"
              value={vendorRoute}
              onChange={(e) => setVendorRoute(e.target.value)}
              placeholder="Leave blank for none"
              className={SD_INPUT}
            />
          )}
          <p className={`mt-1 text-[12px] ${SD_MUTED}`}>
            Tickets filed here are also emailed to this vendor, and their replies come back onto the ticket. Choose None when IT handles these itself.
          </p>
        </div>
        <div>
          <FilterLabel htmlFor="sd-cat-team">Who usually handles it</FilterLabel>
          <input
            id="sd-cat-team"
            value={teamHint}
            onChange={(e) => setTeamHint(e.target.value)}
            className={SD_INPUT}
          />
          <p className={`mt-1 text-[12px] ${SD_MUTED}`}>
            A note about which person or group normally takes these tickets. It is a reminder for the team and does not assign anything.
          </p>
        </div>
      </div>
      <div>
        <FilterLabel htmlFor="sd-cat-pagekey">Page key</FilterLabel>
        <input
          id="sd-cat-pagekey"
          value={pageKey}
          onChange={(e) => setPageKey(e.target.value)}
          placeholder="cockpit:commerce/orders"
          className={SD_INPUT}
        />
        <p className={`mt-1 text-[12px] ${SD_MUTED}`}>
          Links this sub-category to a page in Cockpit or Sweet Spot, for example cockpit:commerce/orders. Reporting a problem from that page lands here.
        </p>
        {node?.page_key && (
          <p className={`mt-1 text-[12px] ${SD_MUTED}`}>
            Current page key: <code>{node.page_key}</code>
          </p>
        )}
      </div>
      <div>
        <label className="flex items-start gap-2 text-[13px] text-amp-ink dark:text-amp-dark-ink">
          <input type="checkbox" checked={hr} onChange={(e) => setHr(e.target.checked)} className="mt-0.5" />
          <span>HR restricted</span>
        </label>
        <p className={`mt-1 pl-6 text-[12px] ${SD_MUTED}`}>
          Hides these tickets from other people at the same store login. Only the requester, the people named on the ticket, and IT can open them. Turn it on for HR forms; the HR tree already has it on.
        </p>
      </div>
      {node && (
        <label className="flex items-center gap-2 text-[13px] text-amp-ink dark:text-amp-dark-ink">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active (people can file here)
        </label>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!canSave || busy}
          className={SD_BTN_PRIMARY}
          onClick={async () => {
            if (node) {
              await update.mutateAsync({ id: node.id, patch: { ...input, is_active: active } });
              onDone(node.id);
            } else {
              const parent = target.mode === 'new' ? target.parent : null;
              await create.mutateAsync({ ...input, parent_id: parent ? parent.id : null });
              onDone(null);
            }
          }}
        >
          {node ? 'Save changes' : 'Add'}
        </button>
        {!node && (
          <button type="button" onClick={() => onDone(null)} className={SD_BTN_GHOST}>
            Cancel
          </button>
        )}
      </div>

      {node && (
        <div className="mt-2 flex flex-col gap-3 border-t border-amp-line pt-3 dark:border-amp-dark-line">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[13px] ${SD_MUTED}`}>Order among its neighbors:</span>
            <button type="button" disabled={idx <= 0 || busy} onClick={() => void reorder(-1)} className={SD_BTN_GHOST}>
              Move up
            </button>
            <button
              type="button"
              disabled={idx < 0 || idx >= siblings.length - 1 || busy}
              onClick={() => void reorder(1)}
              className={SD_BTN_GHOST}
            >
              Move down
            </button>
          </div>
          {moveOptions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <select
                aria-label="Move under"
                value={moveTo}
                onChange={(e) => setMoveTo(e.target.value)}
                className={`${SD_INPUT} max-w-xs`}
              >
                <option value="">Move this to...</option>
                {moveOptions.map((o) => (
                  <option key={o.id ?? 'top'} value={o.id ?? 'top'}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={!moveTo || busy}
                className={SD_BTN_GHOST}
                onClick={async () => {
                  await update.mutateAsync({ id: node.id, patch: { parent_id: moveTo === 'top' ? null : Number(moveTo) } });
                  setMoveTo('');
                }}
              >
                Move
              </button>
            </div>
          )}
          {node.is_active && (
            <div>
              <button
                type="button"
                disabled={busy}
                className={SD_BTN_DANGER}
                onClick={async () => {
                  const open = node.open_ticket_count ?? 0;
                  const note = open > 0 ? ` ${open} open ticket(s) stay as they are.` : '';
                  if (window.confirm(`Deactivate "${node.name}"? People will no longer see it.${note}`)) {
                    await remove.mutateAsync(node.id);
                  }
                }}
              >
                Deactivate
              </button>
              <p className={`mt-1 text-[12px] ${SD_MUTED}`}>
                Deactivating hides it from the form. Move or deactivate anything under it first.
              </p>
            </div>
          )}
        </div>
      )}
      <SdError error={create.error ?? update.error ?? remove.error} what="save the category" />
    </div>
  );
}

